import {
  Box,
  Link,
  Stack,
  useTheme,
  MenuItem,
  Checkbox,
  Typography,
  ListItemText,
  ListItemIcon,
} from "@mui/material";
import { TbShirtSport } from "react-icons/tb";
import { HiOutlineTicket } from "react-icons/hi";
import { useFormContext } from "react-hook-form";
import { OpenInNewRounded } from "@mui/icons-material";
import { MdOutlinedFlag, MdOutlineEventSeat } from "react-icons/md";

import { MLBAdobeScriptHeader } from "@/global/common/mlb-common/MLBAdobeScriptHeader";

export const howItWorks = [
  {
    icon: <MdOutlinedFlag />,
    title: "Choose Your Games",
    description: "Select your Package and the games you would like to attend.",
  },
  {
    icon: <MdOutlineEventSeat />,
    title: "Login to Account Manager",
    description:
      "Click Continue and connect your New York Yankees account using our secure Ticketmaster login.",
  },
  {
    icon: <TbShirtSport />,
    title: "Select Your Suite",
    description: "Select the seats you wish to purchase for each game.",
  },
  {
    icon: <HiOutlineTicket />,
    title: "Get Your Tickets",
    description: "Tickets will be available in your Ballpark App.",
  },
];

export const faqs = [
  {
    title: "When will I receive my tickets?",
    description:
      "If your reservation is accepted, a New York Yankees representative will be in contact with further information, including ticket delivery details.",
  },
  {
    title: "What happens after I purchase a New York Yankees Suite online?",
    description:
      "Following your purchase, a New York Yankees representative will follow up with you to confirm your order and assist with any questions you have in anticipation of your event. Tickets and parking will be added to your New York Yankees account where you'll be able to access them via the MLB Ballpark app. All suite tickets are digital.",
  },
  {
    title: "What is included in my suite?",
    description:
      "If your reservation is accepted, a New York Yankees representative will be in contact with further information, including details regarding parking and food and beverage options.",
  },
  {
    title: "What happens if the postseason game(s) I purchased is not played?",
    description:
      "If the 2026 postseason game(s) is not played, only the postseason commitment for that game(s) will be credited or refunded. All sales for 2027 regular season game(s) are final, and there are no refunds or cancellations.",
  },
  {
    title: "Can I buy a World Series Suite?",
    description: (
      <>
        If you're interested in a World Series package, please email{" "}
        <Link
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
          href="mailto:premium@yankees.com"
        >
          premium@yankees.com
        </Link>
        .
      </>
    ),
  },
];

export function NewYorkYankeesCheckoutTerms() {
  return (
    <>
      I understand and agree to the{" "}
      <Link
        target="_blank"
        sx={{
          fontWeight: 500,
          color: "primary.main",
          textDecoration: "none",
        }}
        href="https://www.mlb.com/yankees/tickets/terms-and-conditions"
      >
        Tickets Terms & Conditions
      </Link>{" "}
      and all the following: The terms and conditions of your ticket and
      policies applicable to use are updated to address{" "}
      <Link
        target="_blank"
        sx={{
          fontWeight: 500,
          color: "primary.main",
          textDecoration: "none",
        }}
        href="https://urldefense.com/v3/__https:/links.mlb.mlbemail.com/s/c/aSJbz_BoMO1CyFE-hqtB6Gzelvcygv_QpDgGABmA6Ym5ZzkHOFBBl_FXvk0_sFYSlKn9asfnCuv_HHYCSf2xvAuccz91KI_9ajBrI1OilqiafuPHEjCN07olvNwdPzAQGjthSC7I4LAQsh1PQ10w8aqylEfsRHFUTlCefBAdsNoTMWW2389FrjN2sBSLyhMES_Fxf3Heip8eyhKyD-qXcScPn_dkmxmp88Fbxa-WK5zTKtb6yoSj8BZJCDefMzz-0xIUKr2eyIqBh6cmZF4yWr4XNCrJ4FSFA6nVe1PFhltS5e8Qk5_lsfg1Qwsd6OGGFjD2oeL9s3K29K8kzdooJgg-wDo6cWu6skZC1cUUjlp9v5KkRGODPOqVkR7SBBETIfS7ZVQpwBxS49DaiczYPS2xxT6gxkbkMwI_ZHBD31yZ27E/FV3JUu83ti-U32e_vNzNjXhw1sMXf__R/8__;!!CtcW_5ORIF_ZIHY!PSLhhAQ3o4p_VusddYyh2Ma_fBD5nIU7W86GnRVX-QVVcEYn3xnaEEx2ZqgZUa9L7ZJ2yNZ72v0_s4SFE5ZnFg$"
      >
        COVID-19 and health & safety
      </Link>
      . By purchasing a New York Yankees ticket, you accept these Terms and
      Conditions and policies applicable to use. Further, due to the COVID-19
      pandemic, scheduling may be impacted; games may be canceled, rescheduled,
      or postponed or games may be held with limited capacity. Upon completing
      your purchase by selecting “Checkout” you acknowledge and agree that your
      credit card will be charged. Time, opponent, date and team rosters and
      lineups are subject to change. The number of innings in a regulation game
      shall be determined by MLB and may be shortened in accordance with MLB
      rules. Licensor makes no representation, warranty and/or guarantee that
      nine (9) innings will be played in any regulation game. In connection with
      your purchase, you will receive ticket-related commercial emails from{" "}
      <Link
        target="_blank"
        sx={{
          fontWeight: 500,
          color: "primary.main",
          textDecoration: "none",
        }}
        href="http://yankees.com/"
      >
        yankees.com
      </Link>
      . You may unsubscribe at any time. By clicking “Checkout”, you agree to
      the{" "}
      <Link
        target="_blank"
        sx={{
          fontWeight: 500,
          color: "primary.main",
          textDecoration: "none",
        }}
        href="https://www.mlb.com/app/ballpark/official-information/privacy-policy"
      >
        MLB Privacy Policy
      </Link>{" "}
      and{" "}
      <Link
        target="_blank"
        sx={{
          fontWeight: 500,
          color: "primary.main",
          textDecoration: "none",
        }}
        href="https://www.mlb.com/app/ballpark/official-information/terms-of-use"
      >
        Terms of Use
      </Link>
      . There are no refunds or exchanges.
    </>
  );
}

export const NewYorkYankeesAdditionalDetailsDisclaimerTC = () => (
  <>
    By entering my name, checking the box above and clicking Submit Request, I
    hereby (i) agree to the{" "}
    <Link
      target="_blank"
      href="https://www.mlb.com/yankees/tickets/terms-and-conditions"
    >
      Terms of Use
    </Link>{" "}
    (ii) agree that my reservation will not be complete unless and until I
    receive a notice from the New York Yankees confirming whether or not my
    reservation has been accepted in accordance with our agreement.
  </>
);

export const NewYorkYankeesTCListItems = () => (
  <Box>
    <MenuItem
      onClick={() => {
        window.open(
          "https://www.mlb.com/yankees/tickets/terms-and-conditions",
          "_blank"
        );
      }}
    >
      <ListItemIcon>
        <OpenInNewRounded fontSize="small" />
      </ListItemIcon>
      <ListItemText>New York Yankees</ListItemText>
    </MenuItem>

    <MenuItem
      onClick={() => {
        window.open(
          "https://www.mlb.com/app/ballpark/official-information/privacy-policy",
          "_blank"
        );
      }}
    >
      <ListItemIcon>
        <OpenInNewRounded fontSize="small" />
      </ListItemIcon>
      <ListItemText>MLB Privacy Policy</ListItemText>
    </MenuItem>

    <MenuItem
      onClick={() => {
        window.open(
          "https://www.mlb.com/app/ballpark/official-information/terms-of-use",
          "_blank"
        );
      }}
    >
      <ListItemIcon>
        <OpenInNewRounded fontSize="small" />
      </ListItemIcon>
      <ListItemText>MLB Terms of Use</ListItemText>
    </MenuItem>
  </Box>
);

export const AnalyticsHeader = () => (
  <>
    <MLBAdobeScriptHeader />
  </>
);

export const RegistrationTerms = () => (
  <>
    By checking this box you agree to receive notifications and offers from the
    New York Yankees and to the{" "}
    <Link
      target={"_blank"}
      sx={{
        color: "primary.main",
        textDecoration: "none",
        fontWeight: 500,
      }}
      href="https://www.attend.tech/terms-of-use"
    >
      Terms of Use
    </Link>{" "}
    and{" "}
    <Link
      target={"_blank"}
      sx={{
        color: "primary.main",
        textDecoration: "none",
        fontWeight: 500,
      }}
      href="https://www.attend.tech/privacy-policy"
    >
      Privacy Policy
    </Link>
    .
  </>
);

export const AdditionalCheckoutInputFields = () => {
  const theme = useTheme();
  const {
    register,
    formState: { errors },
  } = useFormContext();

  return (
    <Stack direction={"row"} alignItems={"flex-start"}>
      <Box>
        <Checkbox
          {...register("secondaryTerms", {
            required: "You need to accept the terms and conditions",
          })}
          sx={{ marginLeft: "-8px", marginTop: "-8px" }}
        />
      </Box>
      <Box>
        <Box>
          <Typography variant="body2" color="text.secondary">
            I understand and agree if the 2026 postseason game(s) is not played,
            only the postseason commitment for that game(s) will be credited or
            refunded. All sales for 2027 regular season game(s) are final, and
            there are no refunds or cancellations.
          </Typography>
        </Box>
        <Box>
          {errors?.secondaryTerms?.message ? (
            <Typography
              variant="caption"
              color={theme.palette.error.main}
              lineHeight={1.1}
            >
              {errors?.secondaryTerms?.message as string}
            </Typography>
          ) : null}
        </Box>
      </Box>
    </Stack>
  );
};
